import { take } from 'rxjs/operators';
import { environment, sessionManager } from '@onviobr/security-services';
import { getAppConfig } from '@onviobr/app-information';
import { datadogRum, } from '@datadog/browser-rum';
import { initLoadTimeTracking } from './performance-tracker.js';
/**
 * Default configuration for scrubbing RUM events.
 * Scrubs all possible data from events.
 * @public
 */
export const defaultScrubConfig = {
    viewUrl: true,
    viewReferrer: true,
    actionTargetName: true,
    errorMessage: true,
    errorStack: true,
    errorResourceUrl: true,
    resourceUrl: true,
};
/**
 * Scrubs sensitive data from Datadog events.
 * Only the attributes listed here can be scrubbed:
 * (https://docs.datadoghq.com/real_user_monitoring/browser/advanced_configuration/?tab=npm#scrub-sensitive-data-from-your-rum-data).
 * Turning an option on (true) scrubs that field.
 * Turning an option off (false) preserves the logging of that field.
 * Defaults each options to false
 *
 * @param e - Datadog Real User Monitoring event (https://docs.datadoghq.com/real_user_monitoring/browser/data_collected/)
 * @param config - fields to should be scrubbed. Every property defaults to true.
 * @public
 */
export function makeDataScrubber(config) {
    config = {
        ...defaultScrubConfig,
        ...(config !== null && config !== void 0 ? config : {}),
    };
    return (e) => {
        var _a, _b;
        const { viewUrl, viewReferrer, actionTargetName, errorMessage, errorStack, errorResourceUrl, resourceUrl } = config;
        if (viewUrl && e.view.url) {
            e.view.url = '';
        }
        if (viewReferrer && e.view.referrer) {
            e.view.referrer = '';
        }
        if (e.action) {
            e = e;
            if (actionTargetName && ((_a = e.action.target) === null || _a === void 0 ? void 0 : _a.name)) {
                e.action.target.name = '';
            }
        }
        if (e.error) {
            e = e;
            if (errorMessage && e.error.message) {
                e.error.message = '';
            }
            if (errorStack && e.error.stack) {
                e.error.stack = '';
            }
            if (errorResourceUrl && ((_b = e.error.resource) === null || _b === void 0 ? void 0 : _b.url)) {
                e.error.resource.url = '';
            }
        }
        if (e.resource) {
            e = e;
            if (resourceUrl && e.resource.url) {
                e.resource.url = '';
            }
        }
    };
}
/**
 * Regex used to allow tracing on onvio origins.
 * Matches any https hostname that contains "onvio."
 * @public
 */
export const ONVIO_DOMAINS = /https:\/\/(.+\.)?onvio\..*/;
/**
 * Regex used to allow tracing from locally hosted apps.
 * Matches any localhost/127.0.0.1/0.0.0.0 hostnames (with or without https)
 * @public
 */
export const LOCAL_DOMAINS = /https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0).*/;
const defaultOnvioRumConfig = {
    applicationId: '115fe201-73c7-4c6b-8844-53f92d331610',
    clientToken: 'pub98867896b8c2b8ac4d4d7cca74e3f929',
    env: environment.environmentName.toLowerCase(),
    allowedTracingOrigins: [
        ONVIO_DOMAINS,
        // Allow traces on locally served apps
        LOCAL_DOMAINS,
    ],
    site: 'datadoghq.com',
    sampleRate: 100,
    trackInteractions: true,
    beforeSend: makeDataScrubber(defaultScrubConfig),
    useSecureSessionCookie: true,
};
/**
 * Installs Datadog Real User Monitoring (RUM) (analytics provider
 * for frontend browser apps). Call when initializing your application.
 * See Datadog RUM docs for more info https://docs.datadoghq.com/real_user_monitoring/.
 * Consumer must register the application's verticalName & version to \@blue/app-info or
 * pass through the service & version.
 *
 * Defaults to setting the applicationId and clientToken for Onvio/OnBalance
 * but allows overriding to set custom a application/client.
 *
 * Defaults service to verticalName registered to \@blue/app-info
 *
 * Defaults Version to version registered to \@blue/app-info
 *
 * Defaults env (ci/demo/qed/prod/local) based on hostname
 *
 * Defaults allowed tracing origins to all *onvio* domains (over https)
 *
 * Defaults site to datadoghq.com
 *
 * Defaults sampleRate to 100
 *
 * Defaults to trackInteractions to true
 *
 * Defaults to scrubbing as much sensitive data as possible.
 * Consumers can override this behavior by passing their own `beforeSend` callback in the datadog configuration.
 * Consumers can create their own `beforeSend` cb using `makeDataScrubber`.
 *
 * Defaults useSecureSessionCookie to true to disable sending rum events on insecure (non-https) connections

 * @param appConfig - Datadog configuration for Real User Monitoring
 *  (https://docs.datadoghq.com/real_user_monitoring/browser/#configuration)
 * @public
 */
export function initializeDatadog(appConfig) {
    const config = {
        ...defaultOnvioRumConfig,
        ...appConfig,
    };
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    config.service = (config.service || getAppConfig().name || '').toLocaleLowerCase();
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    config.version = config.version || getAppConfig().version;
    if (!config.service || !config.version) {
        throw new Error('service and version must be configured');
    }
    datadogRum.init(config);
    if (appConfig === null || appConfig === void 0 ? void 0 : appConfig.useLoadTimeTracking) {
        initLoadTimeTracking();
    }
}
/**
 * Sets the current datadog user as the logged in onvio contact to correlate Datadog logs.
 * Call when the user has been authenticated. Returns function to deactivate a user's
 * context. Call deactivate function on logout
 * @public
 */
export async function setDatadogUserContext(customId) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const id = customId || (await sessionManager.contactID.pipe(take(1)).toPromise());
    datadogRum.setUser({ id });
    // Investigate further: global contexts make the application very (unnecessarily?) chatty
    // datadogRum.addRumGlobalContext('session-bindings', bindings)
    return () => {
        // DatadogRum.removeRumGlobalContext('session-bindings')
    };
}
